import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button, Spinner } from 'react-bootstrap';
import { loginUser, verifyLoginCode, resendLoginCode, cancelLoginChallenge } from '../../redux/thunk/authThunk';
import { setError, setShowLoginModal, setShowRegisterModal, setShowForgotPasswordModal } from '../../redux/slices/authSlice';
import { MixGoatWordmark } from '../MixGoatLogo';
import { useDesignV2 } from '../../utils/designV2';
import SocialAuthButtons from './SocialAuthButtons';
import './LoginModal.scss';

const RESEND_WAIT_SECONDS = 60;

// The email-code step: the password was right but this device is new to the
// account, so the server emailed a six-digit code instead of signing in.
const LoginCodeStep = ({ challenge, isLoading, error }) => {
    const dispatch = useDispatch();
    const [code, setCode] = useState('');
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const secondsSinceSent = Math.floor((now - (challenge.sentAt || now)) / 1000);
    const resendIn = Math.max(0, RESEND_WAIT_SECONDS - secondsSinceSent);

    const submit = (e) => {
        e.preventDefault();
        dispatch(verifyLoginCode({ code }));
    };

    return (
        <>
            <h2 className="auth-title">Check your email</h2>
            <p className="auth-subtitle">
                This device is new to your account. We sent a 6-digit code to{' '}
                <strong>{challenge.email || 'your email'}</strong>.
            </p>

            {error && (
                <div className="auth-alert" role="alert">
                    <span>{error}</span>
                    <button className="auth-alert-close" onClick={() => dispatch(setError(null))}>×</button>
                </div>
            )}

            <Form onSubmit={submit} className="auth-form" noValidate>
                <Form.Group className="auth-field">
                    <Form.Label>Sign-in code</Form.Label>
                    <Form.Control
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="[0-9]*"
                        maxLength={7}
                        placeholder="123456"
                        value={code}
                        onChange={(e) => { setCode(e.target.value.replace(/[^\d\s]/g, '')); if (error) dispatch(setError(null)); }}
                        disabled={isLoading}
                        autoFocus
                        aria-label="Sign-in code"
                    />
                </Form.Group>

                <Button type="submit" className="auth-submit-btn" disabled={isLoading || code.replace(/\D/g, '').length !== 6}>
                    {isLoading ? (<><Spinner size="sm" animation="border" className="me-2" />Verifying...</>) : 'Verify and sign in'}
                </Button>
            </Form>

            <div className="auth-footer">
                <span className="auth-footer-text">
                    Didn't get it?{' '}
                    <button
                        type="button"
                        className="auth-link"
                        onClick={() => dispatch(resendLoginCode())}
                        disabled={isLoading || resendIn > 0}
                    >
                        {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                    </button>
                </span>
            </div>
            <div className="auth-footer">
                <span className="auth-footer-text">
                    <button type="button" className="auth-link" onClick={() => dispatch(cancelLoginChallenge())} disabled={isLoading}>
                        Use a different account
                    </button>
                </span>
            </div>
            <p className="auth-legal">
                Not you? Don't enter the code anywhere — someone knows your password. Reset it from Forgot Password.
            </p>
        </>
    );
};

const LoginModal = () => {
    const dispatch = useDispatch();
    const { isLoading, error, showLoginModal, loginChallenge } = useSelector((state) => state.auth);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const v2 = useDesignV2(); // v2 copy is sentence case

    if (!showLoginModal) return null;

    if (loginChallenge?.challengeId) {
        return (
            <div className="auth-overlay">
                <div className="auth-container">
                    <div className="auth-logo">
                        <MixGoatWordmark height={60} />
                    </div>
                    <LoginCodeStep challenge={loginChallenge} isLoading={isLoading} error={error} />
                </div>
            </div>
        );
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email || !password) {
            dispatch(setError('Please fill in all fields'));
            return;
        }
        dispatch(loginUser({ email, password }));
    };

    const switchToRegister = () => {
        dispatch(setError(null));
        dispatch(setShowLoginModal(false));
        dispatch(setShowRegisterModal(true));
    };

    const switchToForgotPassword = () => {
        dispatch(setError(null));
        if (email.trim()) sessionStorage.setItem('forgotPasswordEmail', email.trim());
        dispatch(setShowLoginModal(false));
        dispatch(setShowForgotPasswordModal(true));
    };

    return (
        <div className="auth-overlay">
            <div className="auth-container">
                <div className="auth-logo">
                    <MixGoatWordmark height={60} />
                </div>

                <h2 className="auth-title">{v2 ? 'Welcome back' : 'Welcome Back'}</h2>
                <p className="auth-subtitle">Log in to continue to MixGoat</p>

                {error && (
                    <div className="auth-alert">
                        <span>{error}</span>
                        <button className="auth-alert-close" onClick={() => dispatch(setError(null))}>×</button>
                    </div>
                )}

                <Form onSubmit={handleSubmit} className="auth-form" noValidate>
                    <Form.Group className="auth-field">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); if (error) dispatch(setError(null)); }}
                            disabled={isLoading}
                            autoFocus
                            autoComplete="email"
                        />
                    </Form.Group>

                    <Form.Group className="auth-field">
                        <Form.Label>Password</Form.Label>
                        <div className="password-input-wrapper">
                            <Form.Control
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); if (error) dispatch(setError(null)); }}
                                disabled={isLoading}
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                tabIndex={-1}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {showPassword ? (
                                        <>
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                                            <line x1="1" y1="1" x2="23" y2="23"/>
                                        </>
                                    ) : (
                                        <>
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                            <circle cx="12" cy="12" r="3"/>
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                    </Form.Group>

                    <div className="auth-forgot-password">
                        <button
                            type="button"
                            className="auth-link"
                            onClick={switchToForgotPassword}
                            disabled={isLoading}
                        >
                            {v2 ? 'Forgot password?' : 'Forgot Password?'}
                        </button>
                    </div>

                    <Button
                        type="submit"
                        className="auth-submit-btn"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                Logging in...
                            </>
                        ) : (
                            v2 ? 'Log in' : 'Log In'
                        )}
                    </Button>
                </Form>

                <SocialAuthButtons />

                <div className="auth-footer">
                    <span className="auth-footer-text">
                        Don't have an account?{' '}
                        <button
                            className="auth-link"
                            onClick={switchToRegister}
                            disabled={isLoading}
                        >
                            {v2 ? 'Sign up' : 'Sign Up'}
                        </button>
                    </span>
                </div>

                <p className="auth-legal">
                    Official MixGoat sign-in. By continuing you agree to our{' '}
                    <button
                        type="button"
                        className="auth-link"
                        onClick={() => window.electron.openUrl('https://mixgoat.com/terms')}
                    >
                        Terms
                    </button>
                    {' '}and{' '}
                    <button
                        type="button"
                        className="auth-link"
                        onClick={() => window.electron.openUrl('https://mixgoat.com/privacy')}
                    >
                        Privacy Policy
                    </button>.
                </p>
            </div>
        </div>
    );
};

export default LoginModal;
